// src/components/products/ProductCard.jsx
import React, { useMemo, useCallback } from "react";

export default function ProductCard({ product, onEdit, onDelete }) {
  const {
    id,
    name,
    brand,
    category,
    category_name, // fallback if your API returns this
    size_label,
    unit_type,
    sku,
    on_hand_units,
    price, // optional legacy field (per unit)
    price_per_unit, // optional
    base_price_per_case,
    case_pack_qty,
    derived_unit_price,
    currency,
    active,
    expiration_date,
    image_url,
    media, // [{url, media_type}]
  } = product || {};

  const _media = media ? JSON.parse(media) : [];
  // ---------- helpers ----------
  const firstImage =
    image_url ||
    (Array.isArray(_media) ? _media.find((m) => m?.url)?.url : null) ||
    null;

  const fmtCurrency = useCallback(
    (val) => {
      const num = Number(val ?? 0);
      if (!isFinite(num)) return "—";
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currency || "INR",
          maximumFractionDigits: 2,
        }).format(num);
      } catch {
        // Fallback if currency code is unknown
        return `₹${num.toFixed(2)}`;
      }
    },
    [currency]
  );

  let stock = on_hand_units;

  const unitPrice = useMemo(() => {
    // prefer derived -> price_per_unit -> price
    const v =
      derived_unit_price ??
      price_per_unit ??
      price ??
      (base_price_per_case && case_pack_qty
        ? Number(base_price_per_case) / Number(case_pack_qty)
        : null);
    return v == null ? null : Number(v);
  }, [
    derived_unit_price,
    price_per_unit,
    price,
    base_price_per_case,
    case_pack_qty,
  ]);

  const casePrice = useMemo(() => {
    if (base_price_per_case == null) return null;
    return Number(base_price_per_case);
  }, [base_price_per_case]);

  const exp = expiration_date ? new Date(expiration_date) : null;
  const expMeta = useMemo(() => {
    if (!exp || isNaN(exp.getTime())) return { label: "—", tone: "neutral" };
    const now = new Date();
    const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: "Expired", tone: "danger" };
    if (days <= 30) return { label: `Expiring in ${days}d`, tone: "warning" };
    return { label: exp.toLocaleDateString(), tone: "ok" };
  }, [exp]);

  const stockMeta = useMemo(() => {
    const s = Number(stock ?? 0);
    if (!isFinite(s)) return { label: "—", tone: "neutral" };
    if (s <= 0) return { label: "Out of stock", tone: "danger" };
    if (s <= 10) return { label: `Low • ${s}`, tone: "warning" };
    return { label: `${s}`, tone: "ok" };
  }, [stock]);

  const categoryLabel = category ?? category_name ?? "—";

  const onDeleteClick = useCallback(() => {
    if (!id) return;
    // Guarded delete
    const nameText = brand
      ? `${brand} ${name ?? ""}`.trim()
      : name ?? "this product";
    if (window.confirm(`Delete ${nameText}? This cannot be undone.`)) {
      onDelete?.(id);
    }
  }, [id, onDelete, brand, name]);

  // ---------- badge helper ----------
  const Badge = ({ tone = "neutral", children, title }) => {
    const tones = {
      neutral: "bg-gray-100 text-gray-700 ring-gray-200",
      ok: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      warning: "bg-amber-50 text-amber-700 ring-amber-200",
      danger: "bg-rose-50 text-rose-700 ring-rose-200",
      info: "bg-blue-50 text-blue-700 ring-blue-200",
    };
    return (
      <span
        title={title}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${
          tones[tone] || tones.neutral
        }`}
      >
        {children}
      </span>
    );
  };

  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white ring-1 ring-gray-100 shadow-sm hover:shadow-xl transition-all">
      {/* Accent bar */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-fuchsia-500 opacity-80" />

      {/* Media */}
      <div className="relative">
        <div className="aspect-[16/9] w-full overflow-hidden rounded-t-2xl bg-gray-50">
          {firstImage ? (
            <img
              src={firstImage}
              alt={name || "Product image"}
              className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray-400">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10.5a2 2 0 0 1-2 2H9l-5 3V5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M8 12l3-3 3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Status chips over image */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          <Badge
            tone={active ? "ok" : "neutral"}
            title={active ? "Active" : "Inactive"}
          >
            {active ? "Active" : "Inactive"}
          </Badge>
          <Badge tone="info" title="Category">
            {categoryLabel}
          </Badge>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Title */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h4 className="truncate text-lg font-semibold text-gray-900">
              {name || "Untitled"}
            </h4>
            {brand ? (
              <span className="text-gray-700 text-sm font-normal">
                {brand}{" "}
              </span>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {size_label ? <Badge title="Size">{size_label}</Badge> : null}
              {unit_type ? <Badge title="Unit">{unit_type}</Badge> : null}
              {sku ? <Badge title="SKU">{sku}</Badge> : null}
            </div>
          </div>

          {/* Price block */}
          <div className="text-right shrink-0">
            <div className="text-sm text-gray-500">Price / unit</div>
            <div className="text-xl font-bold text-gray-900">
              {unitPrice != null ? fmtCurrency(unitPrice) : "—"}
            </div>
            {casePrice != null && case_pack_qty ? (
              <div className="mt-1 text-xs text-gray-500">
                {fmtCurrency(casePrice)} / {case_pack_qty}{" "}
                {unit_type || "units"}
              </div>
            ) : null}
          </div>
        </div>

        {/* Meta grid */}
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="text-gray-500">Stock</div>
            <div className="flex items-center gap-2">
              <Badge
                tone={
                  stockMeta.tone === "danger"
                    ? "danger"
                    : stockMeta.tone === "warning"
                    ? "warning"
                    : "ok"
                }
                title="Inventory status"
              >
                {stockMeta.label}
              </Badge>
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-gray-500">Expiration</div>
            <div>
              <Badge
                tone={
                  expMeta.tone === "danger"
                    ? "danger"
                    : expMeta.tone === "warning"
                    ? "warning"
                    : expMeta.tone === "ok"
                    ? "ok"
                    : "neutral"
                }
                title="Expiration status"
              >
                {expMeta.label}
              </Badge>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-5 h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            {case_pack_qty ? (
              <>
                Case pack:{" "}
                <span className="font-medium text-gray-700">
                  {case_pack_qty}
                </span>
              </>
            ) : (
              <span className="opacity-70">No case info</span>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onEdit?.(product)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              aria-label="Edit product"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 20h9"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M16.5 3.5a2.121 2.121 0 1 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
              Edit
            </button>

            <button
              type="button"
              onClick={onDeleteClick}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400"
              aria-label="Delete product"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 7h16"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M7 7h10l-1 13a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2L7 7Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
